import React, { useState, useEffect, useCallback, useMemo } from 'react';
import useTableSort from '../../hooks/useTableSort';
import SortableHeaderCell from '../../components/common/SortableHeaderCell';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Drawer,
  Divider,
  List,
  ListItem,
  ListItemText,
  Link,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  Block as IsolateIcon,
  LockOpen as ReleaseIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { apiService } from '../../services/apiService';
import { usePermission } from '../../hooks/usePermission';
import { formatDateTime, formatRelativeTime } from '../../utils/dateUtils';
import { severityColors } from '../../theme';
import { getVendorLabel, getVendorIcon, capabilitiesForType, describeApiError } from './agentMeta';

const STATUS_RANK = { isolated: 4, online: 3, offline: 2, unknown: 1 };

/** Key that identifies the same machine across vendors: short hostname (domain stripped), else IP, else vendor id. */
const hostKey = (e) => {
  const h = String(e.hostname || '').trim().toLowerCase().split('.')[0];
  if (h) return `h:${h}`;
  if (e.ip) return `ip:${e.ip}`;
  return `id:${e.agentId}:${e.id}`;
};

/**
 * Collapse the per-agent inventory into one row per machine. The row carries the most
 * recently seen record's fields plus `sources` (every vendor's record) so actions can
 * still target a specific agent.
 */
const mergeEndpoints = (list) => {
  const groups = new Map();
  for (const e of list) { const k = hostKey(e); if (!groups.has(k)) groups.set(k, []); groups.get(k).push(e); }
  return [...groups.values()].map((sources) => {
    const sorted = [...sources].sort((a, b) => new Date(b.lastSeen || 0) - new Date(a.lastSeen || 0));
    const primary = sorted.find((x) => String(x.status).toLowerCase() === 'online') || sorted[0];
    const isolated = sources.some((x) => x.isolated);
    const risk = sources.map((x) => (x.riskScore != null ? Number(x.riskScore) : null)).filter((x) => x != null);
    return {
      ...primary,
      isolated,
      status: isolated ? 'isolated' : primary.status,
      riskScore: risk.length ? Math.max(...risk) : primary.riskScore,
      tags: [...new Set(sources.flatMap((x) => x.tags || []))],
      sources: sorted,
      mergedKey: hostKey(primary),
    };
  });
};

const MERGE_PREF_KEY = 'excubia.endpoints.mergeVendors';

const statusChip = (endpoint) => {
  const status = (endpoint.isolated ? 'isolated' : endpoint.status || 'unknown').toLowerCase();
  const color = status === 'online' ? 'success' : status === 'isolated' ? 'error' : status === 'isolating' ? 'warning' : 'default';
  return <Chip size="small" label={status} color={color} variant={color === 'default' ? 'outlined' : 'filled'} />;
};

const riskLabel = (endpoint) => {
  const parts = [];
  if (endpoint.riskScore !== null && endpoint.riskScore !== undefined) parts.push(`Risk ${endpoint.riskScore}`);
  if (endpoint.exposureLevel) parts.push(`Exposure ${endpoint.exposureLevel}`);
  return parts.length ? parts.join(' · ') : '—';
};

const severityChip = (severity) => {
  const c = severityColors[severity];
  return (
    <Chip
      size="small"
      label={severity || 'Unknown'}
      sx={c ? { bgcolor: c.main, color: c.contrastText } : undefined}
    />
  );
};

/**
 * Endpoint inventory across every enabled agent, with isolate/release actions
 * and a detail drawer.
 */
const EndpointsTab = ({ agents = [], types = [] }) => {
  const { canManageAgents } = usePermission();
  const [endpoints, setEndpoints] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [agentErrors, setAgentErrors] = useState([]);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionDialog, setActionDialog] = useState(null); // { endpoint, action }
  const [comment, setComment] = useState('');
  const [isolationType, setIsolationType] = useState('Full');
  const [actionBusy, setActionBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [mergeVendors, setMergeVendors] = useState(() => { try { return localStorage.getItem(MERGE_PREF_KEY) !== 'false'; } catch (e) { return true; } });
  const { field: sortField, direction: sortDirection, toggle: toggleSort } = useTableSort('hostname');

  const rows = useMemo(() => (mergeVendors ? mergeEndpoints(endpoints) : endpoints.map((e) => ({ ...e, sources: [e] }))), [endpoints, mergeVendors]);
  const toggleMerge = (on) => { setMergeVendors(on); try { localStorage.setItem(MERGE_PREF_KEY, String(on)); } catch (e) { /* ignore */ } };

  // Client-side sort (endpoints are aggregated from every agent in one call)
  const sortedEndpoints = useMemo(() => {
    const valueOf = (row) => {
      switch (sortField) {
        case 'hostname': return String(row.hostname || row.id || '').toLowerCase();
        case 'ip': return String(row.ip || '').split('.').map((o) => o.padStart(3, '0')).join('.');
        case 'os': return `${row.os || ''} ${row.osVersion || ''}`.trim().toLowerCase();
        case 'vendor': return `${(row.sources || []).length} ${(row.sources || []).map((x) => x.agentType).sort().join(',')}`.toLowerCase();
        case 'status': return row.isolated ? STATUS_RANK.isolated : STATUS_RANK[String(row.status || '').toLowerCase()] || 0;
        case 'risk': return row.riskScore != null ? Number(row.riskScore) : row.exposureLevel ? { high: 75, medium: 50, low: 25 }[String(row.exposureLevel).toLowerCase()] || 0 : -1;
        case 'lastSeen': return row.lastSeen ? new Date(row.lastSeen).getTime() : 0;
        case 'tags': return (row.tags || []).length;
        default: return '';
      }
    };
    const dir = sortDirection === 'desc' ? -1 : 1;
    return [...rows].sort((a, b) => { const x = valueOf(a); const y = valueOf(b); if (x < y) return -dir; if (x > y) return dir; return String(a.hostname || '').localeCompare(String(b.hostname || '')); });
  }, [rows, sortField, sortDirection]);

  const load = useCallback(async (opts = {}) => {
    try {
      setLoading(true);
      setError(null);
      const params = { limit: 200 };
      const s = (opts.search !== undefined ? opts.search : search).trim();
      const t = opts.type !== undefined ? opts.type : typeFilter;
      if (s) params.search = s;
      if (t) params.type = t;
      const res = await apiService.agents.getAllEndpoints(params);
      setEndpoints(res.data.endpoints || []);
      setAgentErrors(res.data.errors || []);
    } catch (err) {
      setError(describeApiError(err, 'Failed to load endpoints').message);
    } finally {
      setLoading(false);
    }
  }, [search, typeFilter]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openDetail = async (row) => {
    try {
      setDetailLoading(true);
      setDetail({ ...row });
      const res = await apiService.agents.getEndpoint(row.agentId, row.id);
      setDetail({ ...(res.data.endpoint || row), agentId: row.agentId, agentName: row.agentName, agentType: row.agentType, sources: row.sources });
    } catch (err) {
      setDetail({ ...row, loadError: describeApiError(err, 'Failed to load endpoint details').message });
    } finally {
      setDetailLoading(false);
    }
  };

  const supportsIsolation = (row) => !!capabilitiesForType(row.agentType, types).isolation;

  /** Sources of a row that can perform the action (isolate: any with the capability; release: those currently isolated). */
  const actionSources = (row, action) => (row.sources || [row]).filter((x) => supportsIsolation(x) && (action === 'unisolate' ? x.isolated : true));

  const openAction = (endpoint, action) => {
    const candidates = actionSources(endpoint, action);
    setActionDialog({ endpoint, action, source: candidates[0] || endpoint, candidates });
    setComment('');
    setIsolationType('Full');
  };

  const runAction = async () => {
    if (!actionDialog) return;
    const { action } = actionDialog;
    const endpoint = actionDialog.source || actionDialog.endpoint;
    try {
      setActionBusy(true);
      const res = action === 'isolate'
        ? await apiService.agents.isolateEndpoint(endpoint.agentId, endpoint.id, { comment: comment || undefined, isolationType })
        : await apiService.agents.unisolateEndpoint(endpoint.agentId, endpoint.id, { comment: comment || undefined });
      setNotice(`${endpoint.hostname || endpoint.id} via ${endpoint.agentName || getVendorLabel(endpoint.agentType, types)}: ${res.data.message || `${action} requested`}`);
      setActionDialog(null);
      await load();
      if (detail && detail.id === endpoint.id) openDetail(endpoint);
    } catch (err) {
      setError(describeApiError(err, `Failed to ${action} endpoint`).message);
      setActionDialog(null);
    } finally {
      setActionBusy(false);
    }
  };

  const renderActions = (row) => {
    if (!canManageAgents || !(row.sources || [row]).some(supportsIsolation)) return null;
    return row.isolated ? (
      <Tooltip title="Release from isolation">
        <IconButton size="small" color="success" onClick={(e) => { e.stopPropagation(); openAction(row, 'unisolate'); }}>
          <ReleaseIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    ) : (
      <Tooltip title="Isolate endpoint">
        <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); openAction(row, 'isolate'); }}>
          <IsolateIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    );
  };

  const endpointAgents = agents.filter((a) => a.isEnabled && capabilitiesForType(a.type, types).endpoints);

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search hostname or IP"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') load({ search: e.target.value }); }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ minWidth: 260 }}
        />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Vendor</InputLabel>
          <Select value={typeFilter} label="Vendor" onChange={(e) => { setTypeFilter(e.target.value); load({ type: e.target.value }); }}>
            <MenuItem value="">All vendors</MenuItem>
            {types.map((t) => <MenuItem key={t.type} value={t.type}>{t.label}</MenuItem>)}
          </Select>
        </FormControl>
        <Button variant="outlined" startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />} onClick={() => load()} disabled={loading}>
          Refresh
        </Button>
        <FormControlLabel
          control={<Switch size="small" checked={mergeVendors} onChange={(e) => toggleMerge(e.target.checked)} />}
          label={<Typography variant="body2">Merge hosts across vendors</Typography>}
        />
        <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
          {mergeVendors ? `${rows.length} host(s) · ${endpoints.length} record(s)` : `${endpoints.length} endpoint(s)`} from {endpointAgents.length} agent(s)
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice}</Alert>}
      {agentErrors.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Some agents could not be queried: {agentErrors.map((e) => `${e.agent}: ${e.error}`).join('; ')}
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              {[['hostname', 'Hostname'], ['ip', 'IP'], ['os', 'OS'], ['vendor', mergeVendors ? 'EDR coverage' : 'Vendor / agent'], ['status', 'Status'], ['risk', 'Risk / exposure'], ['lastSeen', 'Last seen'], ['tags', 'Tags']].map(([f, label]) => (
                <SortableHeaderCell key={f} field={f} label={label} sortField={sortField} direction={sortDirection} onSort={toggleSort} />
              ))}
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && endpoints.length === 0 ? (
              <TableRow><TableCell colSpan={9} align="center"><CircularProgress size={24} sx={{ my: 2 }} /></TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    {endpointAgents.length === 0 ? 'No enabled agent supports endpoint listing.' : 'No endpoints found.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : sortedEndpoints.map((row) => (
              <TableRow key={row.mergedKey || `${row.agentId}-${row.id}`} hover sx={{ cursor: 'pointer' }} onClick={() => openDetail(row)}>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{row.hostname || row.id}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{row.ip || '—'}</Typography>
                  {row.externalIp && <Typography variant="caption" color="text.secondary">ext {row.externalIp}</Typography>}
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{row.os || '—'}</Typography>
                  {row.osVersion && <Typography variant="caption" color="text.secondary">{row.osVersion}</Typography>}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(row.sources || [row]).map((src) => (
                      <Tooltip key={`${src.agentId}-${src.id}`} title={`${src.agentName || getVendorLabel(src.agentType, types)} · ${src.isolated ? 'isolated' : src.status || 'unknown'} · seen ${src.lastSeen ? formatRelativeTime(src.lastSeen) : 'n/a'}`}>
                        <Chip
                          size="small"
                          variant={src.isolated ? 'filled' : 'outlined'}
                          color={src.isolated ? 'error' : 'default'}
                          icon={getVendorIcon(src.agentType, { fontSize: 'small' })}
                          label={getVendorLabel(src.agentType, types).replace(' for Endpoint', '').replace('Palo Alto ', '')}
                          onClick={(e) => { e.stopPropagation(); openDetail(src); }}
                        />
                      </Tooltip>
                    ))}
                  </Box>
                </TableCell>
                <TableCell>{statusChip(row)}</TableCell>
                <TableCell><Typography variant="body2">{riskLabel(row)}</Typography></TableCell>
                <TableCell>
                  <Tooltip title={row.lastSeen ? formatDateTime(row.lastSeen) : ''}>
                    <Typography variant="body2">{row.lastSeen ? formatRelativeTime(row.lastSeen) : '—'}</Typography>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 200 }}>
                    {(row.tags || []).slice(0, 4).map((t) => <Chip key={t} size="small" variant="outlined" label={t} />)}
                    {(row.tags || []).length > 4 && <Chip size="small" variant="outlined" label={`+${row.tags.length - 4}`} />}
                  </Box>
                </TableCell>
                <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                  <Tooltip title="Details">
                    <IconButton size="small" onClick={() => openDetail(row)}><ViewIcon fontSize="small" /></IconButton>
                  </Tooltip>
                  {renderActions(row)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Isolate / release confirmation */}
      <Dialog open={!!actionDialog} onClose={() => !actionBusy && setActionDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{actionDialog && actionDialog.action === 'isolate' ? 'Isolate endpoint' : 'Release endpoint'}</DialogTitle>
        <DialogContent>
          {actionDialog && (
            <>
              <Alert severity={actionDialog.action === 'isolate' ? 'warning' : 'info'} sx={{ mb: 2 }}>
                {actionDialog.action === 'isolate'
                  ? `This will cut ${actionDialog.endpoint.hostname || actionDialog.endpoint.id} off from the network (except the EDR console).`
                  : `This will restore network connectivity for ${actionDialog.endpoint.hostname || actionDialog.endpoint.id}.`}
              </Alert>
              {actionDialog.candidates && actionDialog.candidates.length > 1 && (
                <FormControl fullWidth margin="normal" size="small">
                  <InputLabel>Via agent</InputLabel>
                  <Select
                    value={`${actionDialog.source.agentId}-${actionDialog.source.id}`}
                    label="Via agent"
                    onChange={(e) => setActionDialog({ ...actionDialog, source: actionDialog.candidates.find((c) => `${c.agentId}-${c.id}` === e.target.value) })}
                  >
                    {actionDialog.candidates.map((c) => (
                      <MenuItem key={`${c.agentId}-${c.id}`} value={`${c.agentId}-${c.id}`}>{c.agentName || getVendorLabel(c.agentType, types)} ({getVendorLabel(c.agentType, types)})</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {actionDialog.action === 'isolate' && (actionDialog.source || actionDialog.endpoint).agentType === 'microsoft_defender' && (
                <FormControl fullWidth margin="normal" size="small">
                  <InputLabel>Isolation type</InputLabel>
                  <Select value={isolationType} label="Isolation type" onChange={(e) => setIsolationType(e.target.value)}>
                    <MenuItem value="Full">Full</MenuItem>
                    <MenuItem value="Selective">Selective (allow Outlook/Teams/Skype)</MenuItem>
                  </Select>
                </FormControl>
              )}
              <TextField
                fullWidth
                margin="normal"
                label="Comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                multiline
                minRows={2}
                inputProps={{ maxLength: 500 }}
                helperText="Recorded with the action in the vendor console and the activity log."
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialog(null)} disabled={actionBusy}>Cancel</Button>
          <Button
            variant="contained"
            color={actionDialog && actionDialog.action === 'isolate' ? 'error' : 'primary'}
            onClick={runAction}
            disabled={actionBusy}
          >
            {actionBusy ? <CircularProgress size={20} /> : actionDialog && actionDialog.action === 'isolate' ? 'Isolate' : 'Release'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Detail drawer */}
      <Drawer anchor="right" open={!!detail} onClose={() => setDetail(null)} PaperProps={{ sx: { width: { xs: '100%', sm: 480 } } }}>
        {detail && (
          <Box sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" mb={1}>
              <Typography variant="h6" sx={{ flexGrow: 1 }} noWrap>{detail.hostname || detail.id}</Typography>
              {detailLoading && <CircularProgress size={18} sx={{ mr: 1 }} />}
              <IconButton size="small" onClick={() => setDetail(null)}><CloseIcon /></IconButton>
            </Box>
            <Box display="flex" alignItems="center" gap={1} mb={2} flexWrap="wrap">
              {statusChip(detail)}
              {(detail.sources && detail.sources.length > 1 ? detail.sources : [detail]).map((src) => (
                <Chip
                  key={`${src.agentId}-${src.id}`}
                  size="small"
                  variant={src.agentId === detail.agentId && src.id === detail.id ? 'filled' : 'outlined'}
                  icon={getVendorIcon(src.agentType, { fontSize: 'small' })}
                  label={src.agentName || getVendorLabel(src.agentType, types)}
                  onClick={src.agentId === detail.agentId && src.id === detail.id ? undefined : () => openDetail({ ...src, sources: detail.sources })}
                />
              ))}
              {canManageAgents && supportsIsolation(detail) && (
                <Button
                  size="small"
                  variant="outlined"
                  color={detail.isolated ? 'success' : 'error'}
                  startIcon={detail.isolated ? <ReleaseIcon /> : <IsolateIcon />}
                  onClick={() => openAction(detail, detail.isolated ? 'unisolate' : 'isolate')}
                >
                  {detail.isolated ? 'Release' : 'Isolate'}
                </Button>
              )}
            </Box>
            {detail.loadError && <Alert severity="warning" sx={{ mb: 2 }}>{detail.loadError}</Alert>}
            {detail.warning && <Alert severity="info" sx={{ mb: 2 }}>{detail.warning}</Alert>}

            <List dense disablePadding>
              {[
                ['Endpoint ID', detail.id],
                ['IP', [detail.ip, detail.externalIp && `ext ${detail.externalIp}`].filter(Boolean).join(' · ')],
                ['MAC', detail.macAddress],
                ['OS', [detail.os, detail.osVersion].filter(Boolean).join(' ')],
                ['Agent version', detail.agentVersion],
                ['Risk / exposure', riskLabel(detail)],
                ['First seen', detail.firstSeen ? formatDateTime(detail.firstSeen) : null],
                ['Last seen', detail.lastSeen ? formatDateTime(detail.lastSeen) : null],
              ].filter(([, v]) => v).map(([k, v]) => (
                <ListItem key={k} disableGutters>
                  <ListItemText primary={k} secondary={v} primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }} secondaryTypographyProps={{ variant: 'body2', color: 'text.primary' }} />
                </ListItem>
              ))}
            </List>
            {(detail.tags || []).length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                {detail.tags.map((t) => <Chip key={t} size="small" variant="outlined" label={t} />)}
              </Box>
            )}

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>Recent alerts</Typography>
            {!detail.recentAlerts || detail.recentAlerts.length === 0 ? (
              <Typography variant="body2" color="text.secondary">{detailLoading ? 'Loading…' : 'No recent alerts.'}</Typography>
            ) : (
              <List dense disablePadding>
                {detail.recentAlerts.slice(0, 20).map((a, i) => (
                  <ListItem key={a.sourceId || i} disableGutters alignItems="flex-start">
                    <ListItemText
                      primary={(
                        <Box display="flex" alignItems="center" gap={1}>
                          {severityChip(a.severity)}
                          {a.link ? (
                            <Link href={a.link} target="_blank" rel="noopener" variant="body2">{a.title || 'Alert'}</Link>
                          ) : (
                            <Typography variant="body2">{a.title || 'Alert'}</Typography>
                          )}
                        </Box>
                      )}
                      secondary={[a.detectionTime ? formatDateTime(a.detectionTime) : null, a.vendorStatus].filter(Boolean).join(' · ')}
                    />
                  </ListItem>
                ))}
              </List>
            )}

            {Array.isArray(detail.vulnerabilities) && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom>Vulnerabilities ({detail.vulnerabilities.length})</Typography>
                {detail.vulnerabilities.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">None reported.</Typography>
                ) : (
                  <List dense disablePadding>
                    {detail.vulnerabilities.slice(0, 20).map((v) => (
                      <ListItem key={v.cve} disableGutters>
                        <ListItemText
                          primary={(
                            <Box display="flex" alignItems="center" gap={1}>
                              {severityChip(v.severity)}
                              <Typography variant="body2">{v.cve}</Typography>
                              {v.cvss !== undefined && v.cvss !== null && <Typography variant="caption" color="text.secondary">CVSS {v.cvss}</Typography>}
                              {v.exploitAvailable && <Chip size="small" color="error" variant="outlined" label="exploit" />}
                            </Box>
                          )}
                          secondary={v.name}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </>
            )}
          </Box>
        )}
      </Drawer>
    </Box>
  );
};

export default EndpointsTab;
